import StickyFooter from '@lwt-helix/sticky-footer';
import Tab from '@lwt-helix/tab';
import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ShowAdditionalInfo from '../../common/components/table/ShowAdditionalInfo';
import { exportButtonTypes } from '../../common/constants/export';
import { getExportButton } from '../../common/helpers/export';
import { useSearchStore } from '../../store/store';
import { terms } from './constants';
import { CommonBreakdownResults } from './components/CommonBreakdownResults';
import { useBreakdownTable } from './hooks/breakdownTable';
import { getIsOfficeListBreakdown } from './helpers/breakdowns';
import { tableViews } from '../../common/components/table/agGrid/tableViews';
import { useOfficeInfo } from './hooks/useOfficeInfo';
import { OfficeDetails } from './OfficeDetails';
import { tabs } from '../constants/breakdowns';
import { exportTypes } from './helpers/export';
import { csvExportDateFormat } from '../helpers/dataFormatters';
import { OfficeBreakdown } from './OfficeBreakdown';
import PropTypes from 'prop-types';

const Breakdown = ({ setAgentData }) => {
    const firstTabRef = useRef();
    const secondTabRef = useRef();
    const thirdTabRef = useRef();
    const { pathname, state: locationState } = useLocation();
    const isOfficeListBreakdown = getIsOfficeListBreakdown(pathname);
    const fromOfficeBreakdownAndGrowthTab = locationState?.fromOfficeBreakdownAndGrowthTab;

    const gridRefByTab = {
        [tabs.GAINED_AGENTS]: firstTabRef,
        [tabs.LOST_AGENTS]: secondTabRef,
        [tabs.OFFICE_DETAILS]: thirdTabRef,
    };

    const {
        selectedBreakdownTab,
        setSelectedBreakdownTab,
        shouldShowAdditionalInfo,
        isFooterDisabled,
        selectedBreakdownOffice,
    } = useSearchStore((state) => {
        const { [state.selectedModule]: moduleState } = state;
        const { breakdowns = {} } = moduleState;
        const { selectedBreakdownTab, tabsByRoute, selectedBreakdownOffice } = breakdowns;
        const breakdownTabData = tabsByRoute?.[pathname]?.[selectedBreakdownTab];
        return ({
            selectedBreakdownTab,
            setSelectedBreakdownTab: state.setSelectedBreakdownTab,
            shouldShowAdditionalInfo: breakdownTabData?.shouldShowAdditionalInfo ?? false,
            isFooterDisabled: breakdownTabData?.isFooterDisabled ?? false,
            selectedBreakdownOffice,
        });
    });

    const isOfficeDetailsTab = selectedBreakdownTab === tabs.OFFICE_DETAILS;

    const { data, isLoading } = useBreakdownTable({
        parameterName: 'targetOffice',
        pathname,
        isOfficeListBreakdown,
        setAgentData,
    });

    const officeInfoData = useOfficeInfo();

    const setCurrentTab = (currentTab) => {
        setSelectedBreakdownTab(currentTab);
    };

    const officeTabItem = {
        title: terms.officeDetails,
        content: {
            jsx: <div>
                {officeInfoData && <OfficeDetails data={officeInfoData} />}
            </div>
        }
    };
    const officeBreakdownAndGrowthTabItem = {
        title: terms.officeBreakdownAndGrowth,
        content: {
            jsx: <div><OfficeBreakdown gridRef={thirdTabRef}/></div>
        }
    };

    const commonBreakdownResultsProps = {data, isLoading, isOfficeListBreakdown};

    const tabItems = [
        {
            title: terms.gainedAgents,
            content: {
                jsx: (
                    <CommonBreakdownResults
                        {...commonBreakdownResultsProps}
                        gridRef={firstTabRef}
                        tableView={tableViews.agentsGainedBreakdown(shouldShowAdditionalInfo)}
                    />
                )
            }
        },
        {
            title: terms.lostAgents,
            content: {
                jsx: (
                    <CommonBreakdownResults
                        {...commonBreakdownResultsProps}
                        gridRef={secondTabRef}
                        tableView={tableViews.lostAgentsBreakdown(shouldShowAdditionalInfo)}
                    />
                )
            }
        },
        (
            isOfficeListBreakdown
                ? officeBreakdownAndGrowthTabItem : officeTabItem
        )
    ];
    const exportDetails = {
        exportType: fromOfficeBreakdownAndGrowthTab ? exportTypes.fromBreakdownGrowthResults : exportTypes.breakdownResults,
        listingType: 'agents',
        listingStatus: '',
        currentBreakdownTab: tabItems[selectedBreakdownTab],
        office: selectedBreakdownOffice,
        date: csvExportDateFormat(new Date),
        isOfficeListBreakdown,
        fromOfficeBreakdownAndGrowthTab,
    };

    const exportButton = getExportButton({
        exportDetails,
        shouldUsePrimaryColumns: true,
        gridRef: gridRefByTab?.[selectedBreakdownTab].current,
        type: exportButtonTypes.primary,
        isDisabled: isFooterDisabled,
        isTabExport: true
    });

    return (
        <div className="px-3 agent-breakdown-wrapper">
            <Tab
                tabs
                currentIndex={selectedBreakdownTab}
                setCurrentIndex={setCurrentTab}
                className="mt-4 mb-4"
                items={tabItems}
            />
            {!isOfficeDetailsTab && <StickyFooter
                columnProps={[
                    {
                        children: (
                            <div className="ml-auto d-flex w-auto align-self-center">
                                <div className="ml-auto d-flex justify-content-center align-items-center">
                                    <ShowAdditionalInfo />
                                </div>
                                {exportButton}
                            </div>
                        ),
                        sm: 12,
                        align: 'right'
                    }
                ]}
            />}
        </div>
    );
};

Breakdown.propTypes = {
    setAgentData: PropTypes.func.isRequired
};

export default Breakdown;
